import { useEffect, useState } from "react";
import { gql } from '@apollo/client';

import { IDBGroup, IDBMembership, IDBUserType, IGetGroupPermissionsQueryGraphQLResponse, IGetGroupPermissionsQueryVariables, IGetLoggedUserQueryGraphQLResponse, useGetGroupPermissions, useLoggedGeladaLazyQuery, useUser } from "@netfront/gelada-identity-library";
import { IIsLoading } from "@netfront/ui-library";

interface IUseGroups extends Pick<IDBMembership, 'group'>, IIsLoading {
  hasStressManagement?: boolean;
  hasFeedback?: boolean;
  hasHealthCoach?: boolean;
  hasSMS?: boolean;
  isProjectAdmin?: boolean;
  userType?: IDBUserType;
}

export const useGroups = (): IUseGroups => {
  const STRESS_MANAGEMENT_GROUP_IDS: number[] = [9246, 9398, 9399, 9400, 9401, 9406, 9407, 9408, 9409];
  const FEEDBACK_GROUP_IDS: number[] = [9246, 9398, 9399, 9400, 9401, 9402, 9403, 9404, 9405, 10843, 10849, 10850, 10851, 10852, 10853, 10854, 10855, 10856];
  const COACH_GROUP_IDS: number[] = [9246, 9408, 9410, 9412, 9398, 9400, 9402, 9404, 9406];
  const SMS_GROUP_IDS: number[] = [9246, 9407, 9410, 9411, 9398, 9399, 9402, 9403, 9406];

  const { getUser } = useUser();

  const [group, setGroup] = useState<IDBGroup>({} as IDBGroup);
  const [userType, setUserType] = useState<IDBUserType>();
  const [isProjectAdmin, setIsProjectAdmin] = useState<boolean>(false);
  const [hasStressManagement, setHasStressManagement] = useState<boolean>(false);
  const [hasFeedback, setHasFeedback] = useState<boolean>(false);
  const [hasHealthCoach, setHasHealthCoach] = useState<boolean>(false);
  const [hasSMS, setHasSMS] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  const [getGroups] = useLoggedGeladaLazyQuery<IGetLoggedUserQueryGraphQLResponse & IGetGroupPermissionsQueryGraphQLResponse, 
    IGetGroupPermissionsQueryVariables
  >({
    options: {
      onCompleted: ({ membership: { groupPermissions: { edges } }, user: { getLoggedUser: { isProjectAdmin: returnedIsProjectAdmin }} }) => {
        setGroup(edges[0]?.node?.group as IDBGroup);
        
        if (edges?.[0]?.node?.userType) {
          setUserType(edges[0]?.node.userType as IDBUserType);
        };
        
        setIsProjectAdmin(returnedIsProjectAdmin)

        setHasStressManagement(returnedIsProjectAdmin || edges.some(({ node: { group } }) => STRESS_MANAGEMENT_GROUP_IDS.includes((group as IDBGroup).id)));
        setHasFeedback(returnedIsProjectAdmin || edges.some(({ node: { group } }) => FEEDBACK_GROUP_IDS.includes((group as IDBGroup).id)));
        setHasHealthCoach(returnedIsProjectAdmin || edges.some(({ node: { group } }) => COACH_GROUP_IDS.includes((group as IDBGroup).id)));
        setHasSMS(returnedIsProjectAdmin || edges.some(({ node: { group } }) => SMS_GROUP_IDS.includes((group as IDBGroup).id)));
        setIsLoading(false);
      }
    },
    query: gql`
      query getGroupMemberships($after: String, $first: Int, $projectId: String!, $userId: Int!) {
        membership {
          groupPermissions(
            after: $after
            first: $first
            projectId: $projectId
            userId: $userId
          ) {
            edges {
              cursor
              node {
                group {
                  id
                  name
                }
                id
                permision
                userType {
                  id
                  name
                }
              }
            }
          }
        }
      
        user {
          getLoggedUser {
            isProjectAdmin(projectId: $projectId)
          }
        }
      }
    `
  });

  useEffect(() => {
    if (getUser()?.id) {
      getGroups({
        variables: {
          projectId: String(process.env.REACT_APP_PROJECT_ID),
          userId: Number(getUser()?.id),
        }
      })
    } else {
      setIsLoading(false);
    }
  
  }, []);

  return {
    group,
    hasFeedback,
    hasHealthCoach,
    hasSMS,
    hasStressManagement,
    isProjectAdmin,
    userType,
    isLoading
  }
};